/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  Button,
  Card,
  InlineNotification,
  PasswordField,
  Typography2,
  VisuallyHidden,
  breakpoints,
} from '@coursera/cds-core';
import { CheckIcon, CloseIcon } from '@coursera/cds-icons';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import { Fields, Sections, UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { FieldValue, UpdateStatusState } from 'bundles/account-settings/types/Settings';

import _t from 'i18n!nls/account-settings';

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 72;

const getPasswordLengthMessage = (length: number) => {
  if (length < MIN_PASSWORD_LENGTH) {
    const needed = MIN_PASSWORD_LENGTH - length;
    return _t('Password too short, need #{needed} more characters.', { needed });
  }
  if (length > MAX_PASSWORD_LENGTH) {
    const over = length - MAX_PASSWORD_LENGTH;
    return _t('Password too long, #{over} characters over the limit.', { over });
  }
  return _t('Password meets requirements.');
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  headingGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  heading: css`
    margin: 0;
  `,
  subtitle: css`
    margin: 0;
    color: var(--cds-color-neutral-primary-weak);
  `,
  layout: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);

    ${breakpoints.up('md')} {
      flex-direction: row;
      align-items: flex-start;
    }
  `,
  fieldsColumn: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    flex: 1;
    min-width: 0;
  `,
  checklistColumn: css`
    && {
      background: var(--cds-color-neutral-background-primary-weak);
      border: none;
      width: 100%;
      flex: 0 0 auto;
      height: fit-content;
      order: -1;

      ${breakpoints.up('md')} {
        width: 242px;
        order: unset;
      }

      .cds-Card-innerCard {
        padding: var(--cds-spacing-150);
        gap: var(--cds-spacing-50);
        height: auto;
      }
    }
  `,
  checklistHeading: css`
    margin: 0 0 var(--cds-spacing-50) 0;
  `,
  checklistItem: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  buttonRow: css`
    display: flex;
    flex-direction: column;
    align-items: stretch;

    ${breakpoints.up('md')} {
      align-items: flex-start;
    }
  `,
  liveRegion: css`
    width: 100%;

    &:not(:empty) {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  submitButton: css`
    ${breakpoints.only('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
};

type Props = {
  updateStatus: { state: UpdateStatusState };
  invalidFields: FieldValue[];
};

const Password = ({ updateStatus, invalidFields }: Props) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [clientError, setClientError] = useState<'same-password' | null>(null);
  const [passwordRequirementsStatus, setDebouncedLength] = useState<{ announcement: string; hasError: boolean }>({
    announcement: '',
    hasError: false,
  });
  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const clearErrors = () => {
    setClientError(null);
    SettingsActionCreators.resetSection(Sections.PASSWORD);
  };

  const handleChangePassword = () => {
    const passwords = {
      currentPassword,
      newPassword,
      retypedPassword: confirmPassword,
    };

    if (passwords.currentPassword === passwords.newPassword) {
      setClientError('same-password');
    } else {
      setClientError(null);
      SettingsActionCreators.resetPassword(passwords);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleChangePassword();
  };

  const isUpdating = updateStatus.state === UpdateStates.UPDATING;
  const isSucceeded = updateStatus.state === UpdateStates.SUCCEEDED;
  const isFailed =
    clientError !== null ||
    updateStatus.state === UpdateStates.FAILED ||
    updateStatus.state === UpdateStates.FAILED_NEW_PASSWORD_NOT_SUFFICIENTLY_COMPLEX ||
    updateStatus.state === UpdateStates.FAILED_SAME_PASSWORD;

  const errorMessage =
    clientError === 'same-password'
      ? _t('Your new password must be different from your current password.')
      : _t('There was an issue updating your password. Please try again.');

  const successMessage = _t('Your password has been updated.');

  const currentPasswordHasError = invalidFields.includes(Fields.PASSWORD);
  const confirmPasswordHasError = invalidFields.includes(Fields.NEW_PASSWORD);

  useEffect(() => {
    if (currentPasswordHasError) {
      currentPasswordRef.current?.focus();
    } else if (confirmPasswordHasError) {
      confirmPasswordRef.current?.focus();
    }
  }, [currentPasswordHasError, confirmPasswordHasError]);

  const meetsLengthRequirement = newPassword.length >= MIN_PASSWORD_LENGTH && newPassword.length <= MAX_PASSWORD_LENGTH;
  const hasStartedTypingNewPassword = newPassword.length > 0;
  const newPasswordHasLengthError = hasStartedTypingNewPassword && !meetsLengthRequirement;

  useEffect(() => {
    if (!hasStartedTypingNewPassword) {
      setDebouncedLength({ announcement: '', hasError: false });
      return;
    }

    const message = getPasswordLengthMessage(newPassword.length);
    const timeout = setTimeout(() => {
      setDebouncedLength({ announcement: message, hasError: newPasswordHasLengthError });
    }, 1000);
    return () => clearTimeout(timeout);
  }, [newPassword, hasStartedTypingNewPassword, newPasswordHasLengthError]);

  return (
    <form css={styles.container} onSubmit={handleSubmit}>
      <div css={styles.headingGroup}>
        <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
          {_t('Change password')}
        </Typography2>
        <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.subtitle}>
          {_t('Update your password regularly to keep your account secure.')}
        </Typography2>
      </div>

      <div css={styles.layout}>
        <div css={styles.fieldsColumn}>
          <PasswordField
            inputRef={currentPasswordRef}
            data-testid="settings-password-current"
            label={_t('Current password')}
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => {
              setCurrentPassword(event.target.value);
              clearErrors();
            }}
            validationStatus={currentPasswordHasError ? 'error' : undefined}
            validationLabel={currentPasswordHasError ? _t('The password you entered is incorrect.') : undefined}
            necessityIndicator="icon"
            fullWidth
          />
          <PasswordField
            data-testid="settings-password-new"
            label={_t('New password')}
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => {
              setNewPassword(event.target.value);
              clearErrors();
            }}
            necessityIndicator="icon"
            fullWidth
          />
          <PasswordField
            inputRef={confirmPasswordRef}
            data-testid="settings-password-new-confirm"
            label={_t('Confirm new password')}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              clearErrors();
            }}
            validationStatus={confirmPasswordHasError ? 'error' : undefined}
            validationLabel={confirmPasswordHasError ? _t('Passwords do not match.') : undefined}
            necessityIndicator="icon"
            fullWidth
          />
        </div>

        <Card css={styles.checklistColumn} aria-hidden="true">
          <Typography2 variant="subtitleSmall" component="p" css={styles.checklistHeading}>
            {_t('Your new password must be')}
          </Typography2>
          <div css={styles.checklistItem}>
            {meetsLengthRequirement ? (
              <CheckIcon size="small" color="success" />
            ) : (
              <CloseIcon size="small" color="error" />
            )}
            <Typography2 variant="bodySecondary" component="span">
              {_t('Between 8 and 72 characters')}
            </Typography2>
          </div>
        </Card>
        <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
          {passwordRequirementsStatus.announcement}
        </VisuallyHidden>
      </div>

      <div css={styles.buttonRow}>
        <div css={styles.liveRegion} role="alert" aria-live="assertive" aria-atomic="true">
          {passwordRequirementsStatus.hasError && (
            <InlineNotification severity="error">
              {_t('Your password does not meet all the password criteria.')}
            </InlineNotification>
          )}
          {isFailed && !currentPasswordHasError && !confirmPasswordHasError && (
            <InlineNotification severity="error">{errorMessage}</InlineNotification>
          )}
        </div>
        <div
          css={styles.liveRegion}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="password-success-status"
        >
          {isSucceeded && <InlineNotification severity="success">{successMessage}</InlineNotification>}
        </div>
        <Button
          type="submit"
          size="medium"
          variant="primary"
          disabled={!currentPassword || !meetsLengthRequirement || !confirmPassword}
          loading={isUpdating}
          css={styles.submitButton}
        >
          {_t('Change Password')}
        </Button>
      </div>
    </form>
  );
};

export default Password;
